import { PageLayout } from '@/common/layouts/PageLayout.tsx'
import { Button, Col, Form, Row, Typography } from 'antd'
import React from 'react'
import { AntdForm } from '@/common/components/AntdForm'
import { AntdFormRules } from '@/common/components/AntdForm/rules.tsx'
import { Currency } from '@/common/types/region-config.def.ts'
import {
  downloadJsonFile,
  enumMapToSelectOptions,
  stringArrayToSelectOptions,
  termSheetFileName,
} from '@/utils/misc.ts'
import { useCreateTermSheetMutation } from '@/features/document-autofill/document-autofill.api.ts'
import { AntdMultiAdditionalBonus } from '@/pages/document-autofill/contract-sections/multi-additional-bonus-antd.tsx'
import { MacroTermSheetImport } from '@/pages/document-autofill/components/macro-term-sheet-import.tsx'
import { captureScreenshot } from '@/features/document-autofill/contracts/screenshot.tsx'
import { useTermSheetConfig } from '@/pages/document-autofill/hooks/useTermSheetConfig.tsx'

export type MacroTermSheetParam = {
  meta: {
    type: string
    region: string
    candidateType: string
    isHiringManagerPrincipal: boolean
  },
  personalInfo: {
    fullName: string
  }
  employmentDetails: {
    startDate: string,
    position: string,
  },
  financialTerms: {
    salary: {
      currency: string,
      amount: number
    }
  },
  deferredProgram: {
    deferredRate: number,
    compensationThreshold: {
      currency: string,
      amount: number
    },
    vestedYears: number,
    fundName: string
  },
  teamHiring: {
    expenseCap: {
      currency: string,
      amount: number
    },
    initialHeadcount: number,
    hiringLimitDate: string,
    hiringLimitBpThresholdMm: number
  },
  riskParameters: {
    capital: {
      currency: string,
      amount: number
    },
    varLimit: {
      value: number,
      dailyValue: number
    },
    liquidityLimitRate1: number,
    liquidityLimitDays1: number,
    liquidityLimitRate2: number,
    liquidityLimitDays2: number,
    speedBumpLimitRate: number,
    speedBumpBpCutRate: number,
    downAndOutBpRate: number,
    mandate: string,
    mandateDetails: string,
    specificLimits: {
      maxDailyVar: number,
      fxCurrencyStress: {
        lossValue: number,
        shock: number
      },
      equityStress: {
        lossValue: number,
        shock: number
      },
      interestRateFixedIncomeStress: {
        lossValue: number,
        shock: number
      },
      commodityStress: {
        lossValue: number,
        shock: number
      },
      compositeStress: number
    },
    specificFlags: {
      balanceSheet: number,
      numberOfLineItems: number,
      dV01Limit: number,
      maxMaturityFxOptions: number,
      maxMaturityIrOptions: number,
      maxMaturityIrSwaps: number,
      maxMaturityIrSwapsNonCore: number,
      totalOptionPremium: number
    }
  }
}

const initialValues = {
  deferredProgram: {
    deferredRate: 0.25,
    compensationThreshold: {
      currency: 'USD',
      amount: 500000,
    },
    vestedYears: 3,
    fundName: 'Polymer Asia Fund LP',
  },
  riskParameters: {
    specificLimits: {
      maxDailyVar: 0.01,
      fxCurrencyStress: {
        lossValue: 0.04,
        shock: 0.1,
      },
      equityStress: {
        lossValue: 0.04,
        shock: 0.20,
      },
      interestRateFixedIncomeStress: {
        lossValue: 0.03,
        shock: 0.10,
      },
      commodityStress: {
        lossValue: 0.02,
        shock: 0.20,
      },
      compositeStress: 0.05,
    },
    specificFlags: {
      balanceSheet: 0.125,
      numberOfLineItems: 100,
      dV01Limit: 15,
      maxMaturityFxOptions: 2,
      maxMaturityIrOptions: 3,
      maxMaturityIrSwaps: 30,
      maxMaturityIrSwapsNonCore: 10,
      totalOptionPremium: 0.06,
    },
  },
}

export const MacroTermSheet: React.FC = () => {
  const [createTermSheet, { isLoading }] = useCreateTermSheetMutation()
  const [form] = Form.useForm<MacroTermSheetParam>()
  const screenshotRef = React.useRef(null)
  const config = useTermSheetConfig()
  const teamHiringExpenseCap = Form.useWatch(['teamHiring', 'expenseCap'], form)
  const hasSigningBonus = Form.useWatch(['signingBonus'], form)
  const hasAdditionalBonus = Form.useWatch(['hasAdditionalBonus'], form)

  const onFinish = async (data: MacroTermSheetParam) => {
    await createTermSheet({
      type: 'MacroTermSheet',
      payload: data,
    })

    const jsonData = JSON.stringify(
      {
        ...data,
        meta: {
          type: 'macro-term-sheet',
          region: config.region,
          candidateType: config.candidateType,
          isHiringManagerPrincipal: config.isHiringManagerPrincipal,
        },
      }, null, 2)
    downloadJsonFile(jsonData, termSheetFileName(data.personalInfo.fullName, 'Macro'))
    captureScreenshot(screenshotRef, `TermSheet_${data.personalInfo.fullName}`)
  }

  const FundNameCategory: Record<string, string> = {
    'Polymer Asia Fund LP': 'Polymer Asia Fund LP',
  }

  const FundNameOptions = enumMapToSelectOptions(FundNameCategory)

  return (
    <div ref={screenshotRef}>
      <PageLayout>
        <Typography.Title level={2}>Macro Term Sheet</Typography.Title>
        <MacroTermSheetImport form={form} />
        <Form
          layout="vertical"
          className="space-y-4"
          scrollToFirstError
          form={form}
          initialValues={initialValues}
          onFinish={onFinish}
        >
          <Typography.Title level={4}>Personal Info</Typography.Title>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.Input
                fieldProps={{
                  name: ['personalInfo', 'fullName'],
                  rules: [AntdFormRules.required],
                  label: 'Full Name',
                }}
              />
            </Col>
          </Row>
          <Typography.Title level={4}>Employment Details</Typography.Title>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.DatePicker
                fieldProps={{
                  name: ['employmentDetails', 'startDate'],
                  label: 'Start Date',
                  rules: [AntdFormRules.required],
                }}
                format={'MMMM D, YYYY'}
              />
            </Col>
            <Col>
              <AntdForm.Input
                fieldProps={{
                  name: ['employmentDetails', 'position'],
                  label: 'Position',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
            <Col>
              <AntdForm.Input
                fieldProps={{
                  name: ['employmentDetails', 'location'],
                  label: 'Location',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
          </Row>
          <Typography.Title level={4}>Financial Terms</Typography.Title>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.MoneyInput
                currencyOptions={config.baseSalaryCcyOptions}
                fieldProps={{
                  name: ['financialTerms', 'salary'],
                  label: 'Base Salary',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
          </Row>
          <Typography.Title level={5}>Performance Bonus</Typography.Title>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.NumberInput
                percent={true}
                fieldProps={{
                  name: ['financialTerms', 'performanceBonus', 'payoutRate'],
                  label: 'Payout Rate',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
          </Row>
          <Typography.Title level={5}>Signing Bonus</Typography.Title>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.Boolean
                fieldProps={{
                  name: ['signingBonus'],
                  label: 'Has Signing Bonus',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
          </Row>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.Switch
                fieldProps={{
                  name: ['financialTerms', 'signingBonus', 'subjectToPrevIncomeProof'],
                  label: 'Subject to Previous Income Proof',
                  rules: [AntdFormRules.requiredIf(hasSigningBonus)],
                }}
              />
            </Col>
            <Col>
              <AntdForm.Select
                options={stringArrayToSelectOptions(config.bonusCcyOptions)}
                fieldProps={{
                  name: ['financialTerms', 'signingBonus', 'currency'],
                  label: 'Currency',
                  rules: [AntdFormRules.requiredIf(hasSigningBonus)],
                }}
                disabled={!hasSigningBonus}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['financialTerms', 'signingBonus', 'amount'],
                  label: 'Amount',
                  rules: [AntdFormRules.requiredIf(hasSigningBonus)],
                }}
                disabled={!hasSigningBonus}
              />
            </Col>
            <Col>
              <AntdForm.Input
                fieldProps={{
                  name: ['financialTerms', 'signingBonus', 'payableOn'],
                  label: 'Payable On',
                  rules: [AntdFormRules.requiredIf(hasSigningBonus)],
                  extra: <Typography.Text type="secondary">E.g. First payroll run</Typography.Text>,
                }}
                disabled={!hasSigningBonus}
              />
            </Col>
          </Row>
          <Typography.Title level={5}>Additional Bonus</Typography.Title>
          <AntdMultiAdditionalBonus
            hasAdditionalBonus={hasAdditionalBonus}
            parentName="financialTerms"
            currencyOptions={config.bonusCcyOptions}
          />

          <Typography.Title level={4}>Deferred Program</Typography.Title>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.NumberInput
                percent={true}
                fieldProps={{
                  name: ['deferredProgram', 'deferredRate'],
                  label: 'Deferred Rate',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
            <Col>
              <AntdForm.MoneyInput
                currencyOptions={config.usdOnly}
                fieldProps={{
                  name: ['deferredProgram', 'compensationThreshold'],
                  label: 'Compensation Threshold',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['deferredProgram', 'vestedYears'],
                  label: 'Vested Years',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
            <Col>
              <AntdForm.Select
                options={FundNameOptions}
                fieldProps={{
                  name: ['deferredProgram', 'fundName'],
                  label: 'Fund Name',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
          </Row>
          <Typography.Title level={4}>Team Hiring</Typography.Title>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.MoneyInput
                currencyOptions={config.allCcyOptions}
                fieldProps={{
                  name: ['teamHiring', 'expenseCap'],
                  label: 'Expense Cap',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['teamHiring', 'initialHeadcount'],
                  label: 'Initial Headcount',
                  rules: [AntdFormRules.requiredIf(teamHiringExpenseCap && teamHiringExpenseCap.amount !== 0)],
                }}
              />
            </Col>
            <Col>
              <AntdForm.DatePicker
                fieldProps={{
                  name: ['teamHiring', 'hiringLimitDate'],
                  label: 'Hiring Limit Date',
                  extra: <Typography.Text type="secondary">If not applicable, leave blank</Typography.Text>,
                }}
                format={'MMMM D, YYYY'}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['teamHiring', 'hiringLimitBpThresholdMm'],
                  label: 'Hiring Limit BP Threshold ($MM)',
                  extra: <Typography.Text type="secondary">In USD million</Typography.Text>,
                  rules: [AntdFormRules.requiredIf(teamHiringExpenseCap && teamHiringExpenseCap.amount !== 0)],
                }}
              />
            </Col>
          </Row>
          <Typography.Title level={4}>Risk Parameters</Typography.Title>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.MoneyInput
                currencyOptions={[Currency.USD]}
                fieldProps={{
                  name: ['riskParameters', 'capital'],
                  label: 'Capital',
                  rules: [AntdFormRules.required],
                  extra: <Typography.Text type="secondary">In USD million</Typography.Text>,
                }}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'varLimit', 'value'],
                  label: 'VaR Limit',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'varLimit', 'dailyValue'],
                  label: 'Daily VaR Limit',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
          </Row>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'liquidityLimitRate1'],
                  label: 'Liquidity Limit Rate 1',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'liquidityLimitDays1'],
                  label: 'Liquidity Limit Days 1',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'liquidityLimitRate2'],
                  label: 'LiquidityLimitRate2',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'liquidityLimitDays2'],
                  label: 'Liquidity Limit Days 2',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
          </Row>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'speedBumpBpLimitRate'],
                  label: 'Speed Bump BP Limit Rate',
                  rules: [AntdFormRules.required],
                  extra: <Typography.Text type="secondary">Put zero if not applicable</Typography.Text>,
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'speedBumpBpCutRate'],
                  label: 'Speed Bump BP Cut Rate',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'downAndOutBpRate'],
                  label: 'Down and Out BP Rate',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.Input
                fieldProps={{
                  name: ['riskParameters', 'mandate'],
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
            <Col>
              <AntdForm.Input
                fieldProps={{
                  name: ['riskParameters', 'mandateDetails'],
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
          </Row>
          <Typography.Title level={5}>Specific Limits</Typography.Title>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificLimits', 'maxDailyVar'],
                  label: 'Max Daily VAR',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificLimits', 'fxCurrencyStress', 'lossValue'],
                  label: 'Fx Currency Stress Loss',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificLimits', 'fxCurrencyStress', 'shock'],
                  label: 'Fx Currency Stress Shock',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
          </Row>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificLimits', 'equityStress', 'lossValue'],
                  label: 'Equity Stress Loss Value',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificLimits', 'equityStress', 'shock'],
                  label: 'Equity Stress Shock',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificLimits', 'interestRateFixedIncomeStress', 'lossValue'],
                  label: 'Interest Rate/Fixed Income Stress Loss Value',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificLimits', 'interestRateFixedIncomeStress', 'shock'],
                  label: 'Interest Rate/Fixed Income Stress Shock',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
          </Row>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificLimits', 'commodityStress', 'lossValue'],
                  label: 'Commodity Stress Loss Value',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificLimits', 'commodityStress', 'shock'],
                  label: 'Commodity Stress Shock',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificLimits', 'compositeStress'],
                  label: 'Composite Stress Less than',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
          </Row>
          <Typography.Title level={5}>Specific Flags</Typography.Title>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificFlags', 'balanceSheet'],
                  label: 'Balance Sheet < % of Capital',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificFlags', 'numberOfLineItems'],
                  label: 'Number of Line Items',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificFlags', 'dV01Limit'],
                  label: 'DV01 Limit',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificFlags', 'maxMaturityFxOptions'],
                  label: 'Max Maturity Fx Options (Yrs)',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
          </Row>
          <Row gutter={[20, 10]}>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificFlags', 'maxMaturityIrOptions'],
                  label: 'Max Maturity Ir Options (Yrs)',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificFlags', 'maxMaturityIrSwaps'],
                  label: 'Max Maturity Ir Swaps (Yrs)',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificFlags', 'maxMaturityIrSwapsNonCore'],
                  label: 'Max Maturity Ir Swaps Non Core (Yrs)',
                  rules: [AntdFormRules.required],
                }}
              />
            </Col>
            <Col>
              <AntdForm.NumberInput
                fieldProps={{
                  name: ['riskParameters', 'specificFlags', 'totalOptionPremium'],
                  label: 'Total Option Premium',
                  rules: [AntdFormRules.required],
                }}
                percent={true}
              />
            </Col>
          </Row>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Submit
          </Button>
        </Form>
      </PageLayout>
    </div>
  )
}
